import React from 'react';
import { Link } from 'react-router-dom';
import { mockData } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Award, Users, Target, Heart } from 'lucide-react';
import { Helmet } from "react-helmet-async";

<Helmet>
  <title>About | Skyreti EduCore</title>
  <meta
    name="description"
    content="Meet the founder of Skyreti EduCore and learn about our mission."
  />
</Helmet>

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-20 px-4 bg-primary-lime">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-black mb-6">
            About Skyretieducore
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            We're a team of passionate creators dedicated to transforming digital experiences across education, business, and innovation.
          </p>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <Target className="text-black" size={32} />
                <h2 className="font-serif text-3xl font-bold text-black">Our Mission</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                To democratize access to world-class digital solutions by making cutting-edge technology 
                accessible to schools, businesses, and creators of all sizes. We believe every organization 
                deserves the tools to thrive in the digital age.
              </p>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <Heart className="text-black" size={32} />
                <h2 className="font-serif text-3xl font-bold text-black">Our Values</h2>
              </div>
              <ul className="space-y-3 text-lg text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-black">Excellence:</span>
                  We never compromise on quality
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-black">Innovation:</span>
                  Always pushing boundaries
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-black">Integrity:</span>
                  Honest and transparent always
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-black">Impact:</span>
                  Creating lasting positive change
                </li>
              </ul>
            </div>
          </div>

          {/* Company Story */}
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-4xl font-bold text-black text-center mb-8">Our Story</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Founded in 2019, Skyretieducore began with a simple observation: schools and startups 
                were struggling to find affordable, high-quality digital solutions. The market was 
                divided between expensive agencies and unreliable freelancers.
              </p>
              <p>
                We set out to bridge that gap. Starting with a small team of three passionate developers 
                and designers, we built our first educational platform for a local school district. The 
                success of that project opened doors, and word spread quickly.
              </p>
              <p>
                Today, we're proud to have served over 250 clients across 15 countries, delivering 
                everything from simple landing pages to complex enterprise applications. But we haven't 
                lost sight of our roots—every project, regardless of size, gets the same dedication and 
                expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <Users className="text-black" size={32} />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-black mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The talented people behind the projects you love
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.team.map((member, index) => (
              <Card key={index} className="bg-white border-none hover:shadow-lg transition-shadow text-center">
                <CardHeader>
                  <div className="w-24 h-24 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-white">{member.initials}</span>
                  </div>
                  <CardTitle className="font-serif text-xl text-black">
                    {member.name}
                  </CardTitle>
                  <p className="text-sm font-medium text-gray-600">{member.role}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                    {member.bio}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {member.specialties.map((specialty, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-card-lime text-xs">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <Award className="text-black" size={32} />
            </div>
            <h2 className="font-serif text-4xl font-bold text-black mb-4">
              Awards & Recognition
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.awards.map((award, index) => (
              <Card key={index} className="bg-card-lime border-none text-center">
                <CardContent className="pt-8">
                  <Award className="mx-auto mb-4 text-black" size={48} />
                  <h3 className="font-serif text-xl font-bold text-black mb-2">
                    {award.title}
                  </h3>
                  <p className="text-gray-700">{award.organization}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-4 bg-black text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockData.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-serif text-4xl md:text-5xl font-bold mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-primary-lime">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-serif text-4xl font-bold text-black mb-6">
            Want to Join Our Team?
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            We're always looking for talented people to join our growing team
          </p>
          <Button className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg" asChild>
            <Link to="/careers">View Open Positions</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
